import axios from 'axios';
import { isLoading, loadingCount } from '../state/loadingSignal';
import {CORE_API_BASE_URL} from "../env";

/**
 * @param {Error} error - The error object from Axios.
 * @param {string} method - The HTTP method used (e.g., 'GET', 'POST').
 * @param {string} url - The URL that was called.
 * @returns {Error} - A new, more informative Error object.
 */
const handleError = (error, method, url) => {
  console.error(`ERROR during ${method} to ${url}:`, error);

  let errorMessage = "An unexpected error occurred.";
  let status = null;

  if (axios.isAxiosError(error) && error.response) {
    status = error.response.status;
    const responseData = error.response.data;

    console.error("Error Status:", status);
    console.error("Error Response Data:", responseData);

    errorMessage = responseData.status || responseData.message || `Server responded with status ${status}.`;

    if (status === 404) {
      errorMessage = "The requested resource was not found.";
    } else if (status === 500) {
      errorMessage = "An internal server error occurred. Please try again later.";
    }
  } else if (error.request) {
    errorMessage = "Could not connect to the server. Please check your network connection.";
  } else {
    errorMessage = error.message;
  }

  const serviceError = new Error(errorMessage);
  serviceError.status = status;
  throw serviceError;
};

const headers = {
  'Content-Type': 'application/json',
  'Accept': 'application/json'
};

/**
 * A generic function for making GET requests.
 * @param {string} resource - The API resource to call
 * @param {Object} paramsData - Query parameters for the request.
 * @param {boolean} showSpinner - Whether to show a global loading spinner.
 * @returns {Promise<any>} - The data from the API response.
 */
async function serviceCallGet(resource, paramsData = {}, showSpinner = true) {
  const url = `${CORE_API_BASE_URL}${resource}`;

  if (showSpinner) {
    isLoading.value = true;
    loadingCount.value++;
  }

  try {
    const response = await axios.get(url, {
      params: paramsData,
      headers
    });
    return response.data.results || response.data.result || response.data;
  } catch (error) {
    handleError(error, 'GET', url);
  } finally {
    if (showSpinner) {
      loadingCount.value--;
      if (loadingCount.value <= 0) {
        isLoading.value = false;
        loadingCount.value = 0;
      }
    }
  }
}

/**
 * A generic function for making POST requests.
 * @param {string} resource - The API endpoint to call.
 * @param {Object} postData - The data to send in the request body.
 * @param {boolean} showSpinner - Whether to show a global loading spinner.
 * @returns {Promise<any>} - The data from the API response.
 */
async function serviceCallPost(resource, postData = {}, showSpinner = true) {
  const url = `${CORE_API_BASE_URL}${resource}`;

  if (showSpinner) {
    isLoading.value = true;
    loadingCount.value++;
  }

  try {
    const response = await axios.post(url, postData, {headers});
    return response.data;
  } catch (error) {
    handleError(error, 'POST', url);
  } finally {
    if (showSpinner) {
      loadingCount.value--;
      if (loadingCount.value <= 0) {
        isLoading.value = false;
        loadingCount.value = 0;
      }
    }
  }
}

// --- Specific API Functions ---

/**
 * Retrieves a list of referrals.
 * @param {Object} params - Query parameters like limit, offset, sortBy.
 * @returns {Promise<Array>} - A promise that resolves to the list of referrals.
 */
const getReferrals = (params = {}) => {
  return serviceCallGet('/referrals', params);
};

/**
 * Retrieves a single referral by its code.
 * @param {string} code - The referral code.
 * @returns {Promise<Object>} - A promise that resolves to the referral object.
 */
const getReferralByCode = (code) => {
  if (!code) throw new Error("Referral code is required.");
  return serviceCallGet(`/referrals/${code}`);
};

/**
 * Creates a new customer registration.
 * @param {Object} registrationData - The registration data to be submitted.
 * @returns {Promise<Object>} - A promise that resolves to the newly created registration object.
 */
const createRegistration = (registrationData) => {
  return serviceCallPost('/registrations', registrationData);
};

export const coreService = {
  getReferrals,
  getReferralByCode,
  createRegistration,
};
